import App from 'antd/lib/app';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ICreateSavedFilter, ISavedFilter, SavedFilterSubType } from '../../../../../components/sidebar-filters/saved-filter/interface';
import { useSavedFiltersList } from '../../../../../components/sidebar-filters/useSavedFiltersList';
import { DEFAULT_PAGINATION } from '../../../../../domain/commonConst';
import {
    useSavedFilterCreateMutation,
    useSavedFilterDeleteMutation,
    useSavedFilterUpdateMutation,
} from '../../../../../domain/saved-filters/queries';
import { useSidebarFilterOptionsContext } from '../../../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { usePageLocation } from '../../../../usePageState';
import {
    checkFilterInvalidValues,
    countNotEmptyFilters,
    transformVendorBillItemsSavedFilterValuesFromDto,
    transformVendorBillItemsSavedFilterValuesToDto,
} from './utils';
import { IVendorBillItemsPageState } from '../VendorBillItems';

export interface IVendorBillItemsSidebarFilters {
    period?: string;
    markets?: string[];
    utilities?: string[];
    programs?: string[];
    transactionIds?: string[];
    event?: string;
}

export interface IVendorBillItemsSidebarFiltersContext {
    filtersCount: number;
    filters: IVendorBillItemsSidebarFilters;
    savedFilters: ISavedFilter[];

    isSavedFiltersLoading: boolean;

    handleFilterChange: (key: string, value: any) => void;
    handleFiltersClear: () => void;
    handleDefaultFilters: (filters: IVendorBillItemsSidebarFilters) => void;

    selectedSavedFilter: ICreateSavedFilter | null;
    isCreateFilterLoading: boolean;
    isUpdateFilterLoading: boolean;
    isDeleteFilterLoading: boolean;

    handleSavedFilterSelect: (filter: ICreateSavedFilter | null) => void;
    handleCreateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleUpdateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleDeleteSavedFilter: (filter: ISavedFilter) => Promise<void>;

    appliedSavedFilter: ISavedFilter | null; // Using for applying filters
    handleApplySavedFilter: (savedFilter: ISavedFilter) => void;
}

export const VendorBillItemSidebarFiltersContext = React.createContext<IVendorBillItemsSidebarFiltersContext>(
    {} as IVendorBillItemsSidebarFiltersContext
);

export const convertVendorBillItemsPageStateToFilterState = (pageState: IVendorBillItemsPageState): IVendorBillItemsSidebarFilters => {
    const result: IVendorBillItemsSidebarFilters = {};
    const filterData = pageState.filter || {};

    // Convert period
    if (filterData.period !== undefined) {
        result.period = filterData.period;
    }
    // Convert markets
    if (filterData.markets !== undefined) {
        result.markets = Array.isArray(filterData.markets) ? filterData.markets : [filterData.markets];
    }

    // Convert utilities
    if (filterData.utilities !== undefined) {
        result.utilities = Array.isArray(filterData.utilities) ? filterData.utilities : [filterData.utilities];
    }

    // Convert program
    if (filterData.programs !== undefined) {
        result.programs = Array.isArray(filterData.programs) ? filterData.programs : [filterData.programs];
    }

    // Convert transactionIds
    if (filterData.transactionIds !== undefined) {
        result.transactionIds = Array.isArray(filterData.transactionIds) ? filterData.transactionIds : [filterData.transactionIds];
    }

    // Convert event
    if (filterData.event !== undefined) {
        result.event = filterData.event;
    }

    return result;
};

export const VendorBillItemSidebarFiltersProvider = ({ children }: { children: React.ReactNode }) => {
    const { filtersCount, filters, handleFilterChange, handleFiltersChange, handleFiltersClear } = useSidebarFiltering();

    // Saving Filters
    const {
        savedFilters,
        isSavedFiltersLoading,
        selectedSavedFilter,
        appliedSavedFilter,
        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    } = useVendorBillItemsSavedFilters();

    const onFiltersChange = useCallback(
        (key: string, value: unknown) => {
            handleFilterChange(key, value);
            handleApplySavedFilter(null); // Reset applied filter when filters change
        },
        [handleApplySavedFilter, handleFilterChange]
    );

    const onDefaultFiltersApply = useCallback(
        (filters: IVendorBillItemsSidebarFilters) => {
            handleFiltersChange(filters);
        },
        [handleFiltersChange]
    );

    // Combine logic for applying saved filters
    const onApplySavedFilter = useCallback(
        (savedFilter: ISavedFilter) => {
            const filterValues = savedFilter.filter_values;

            handleFiltersChange(filterValues);
            handleApplySavedFilter(savedFilter);
        },
        [handleApplySavedFilter, handleFiltersChange]
    );

    const onFiltersClear = useCallback(() => {
        handleFiltersClear();
        handleApplySavedFilter(null);
    }, [handleApplySavedFilter, handleFiltersClear]);

    return (
        <VendorBillItemSidebarFiltersContext.Provider
            value={{
                filtersCount,
                filters,
                savedFilters,

                isSavedFiltersLoading,

                handleFilterChange: onFiltersChange,
                handleFiltersClear: onFiltersClear,
                handleDefaultFilters: onDefaultFiltersApply,

                selectedSavedFilter,
                isCreateFilterLoading,
                isUpdateFilterLoading,
                isDeleteFilterLoading,
                handleSavedFilterSelect,
                handleCreateSavedFilter,
                handleUpdateSavedFilter,
                handleDeleteSavedFilter,

                appliedSavedFilter,
                handleApplySavedFilter: onApplySavedFilter,
            }}
        >
            {children}
        </VendorBillItemSidebarFiltersContext.Provider>
    );
};

const useSidebarFiltering = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation();
    const pageState: IVendorBillItemsPageState = queryToState(location.search);

    const [filters, setFilters] = useState<IVendorBillItemsSidebarFilters>(convertVendorBillItemsPageStateToFilterState(pageState));
    const filtersCount = useMemo(() => countNotEmptyFilters(filters), [filters]);

    const handleFilterChange = (key: string, value: unknown) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));

        // Update the URL query parameters with filter object structure
        const updatedPageState = {
            ...pageState,
            filter: {
                ...pageState.filter,
                [key]: value,
            },
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        };

        setPageQuery(updatedPageState);
    };

    const handleFiltersChange = (newFilters: IVendorBillItemsSidebarFilters) => {
        setFilters(newFilters);
        setPageQuery({
            ...pageState,
            filter: newFilters,
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        });
    };

    const handleFiltersClear = () => {
        setFilters({});

        setPageQuery({
            ...pageState,
            filter: {},
        });
    };

    return {
        filtersCount,
        filters,

        handleFilterChange,
        handleFiltersChange,
        handleFiltersClear,
    };
};

const useVendorBillItemsSavedFilters = () => {
    const { notification } = App.useApp();
    const context = useSidebarFilterOptionsContext();

    // This filter state using to track applied saved filter
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);

    // This filter state using to create/update saved filter
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<ICreateSavedFilter | null>(null);
    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.vendorBillItems);

    const { mutateAsync: createSavedFilter, isLoading: isCreateFilterLoading } = useSavedFilterCreateMutation();
    const { mutateAsync: deleteSavedFilter, isLoading: isDeleteFilterLoading } = useSavedFilterDeleteMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdateFilterLoading } = useSavedFilterUpdateMutation();

    const validateSavedFilter = useCallback(
        (filter: ISavedFilter) => {
            // Transform filter values from DTO format to Interface
            filter.filter_values = transformVendorBillItemsSavedFilterValuesFromDto(filter.filter_values);

            const outdatedFields = checkFilterInvalidValues(filter.filter_values, context);
            // Check if the filter has not existing values in options
            filter.isOutdated = outdatedFields.length > 0;
            filter.outdatedFields = outdatedFields;

            return filter;
        },
        [context]
    );

    const validatedSavedFilters = useMemo(() => {
        if (savedFilters.length === 0) return [];

        return savedFilters.map((filter: any) => validateSavedFilter(filter));
    }, [savedFilters, validateSavedFilter]);

    const handleSavedFilterSelect = (filter: ICreateSavedFilter | null) => {
        setSelectedSavedFilter(filter);
    };

    const handleApplySavedFilter = (savedFilter: ISavedFilter | null) => {
        setAppliedSavedFilter(savedFilter);
    };

    const handleCreateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformVendorBillItemsSavedFilterValuesToDto(filterData.filter_values);

            await createSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot save filter!' });
        }
    };

    const handleUpdateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformVendorBillItemsSavedFilterValuesToDto(filterData.filter_values);

            await updateSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot update filter!' });
        }
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        try {
            await deleteSavedFilter(filter.id);

            if (selectedSavedFilter?.id === filter.id) {
                setSelectedSavedFilter(null);
            }

            if (appliedSavedFilter?.id === filter.id) {
                setAppliedSavedFilter(null);
            }
        } catch (err) {
            notification.error({ message: 'Cannot delete filter!' });
        }
    };

    return {
        savedFilters: validatedSavedFilters,
        selectedSavedFilter,
        appliedSavedFilter,
        isSavedFiltersLoading,
        validateSavedFilter,

        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,

        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    };
};

export const useVendorBillItemsSidebarFiltersContext = () => {
    const context = React.useContext(VendorBillItemSidebarFiltersContext);
    if (!context) {
        throw new Error('useVendorBillItemsSidebarFiltersContext must be used within VendorBillItemSidebarFiltersContext');
    }

    return context;
};
